import { useEffect } from 'react'
import apiClient from '@/lib/api'
import {
  clearRegistrationSessionStartup,
  loadRegistrationSessionStartup,
  registrationStartupDecision,
  registrationStartupFailureDecision,
  registrationStartupRetryDelay,
  REGISTRATION_STARTUP_QUEUED_EVENT,
} from '@/lib/registrationSessionStartup'

const POLL_INTERVAL_MS = 5_000
const ACCEPTED_START_COOLDOWN_MS = 30_000

function responseStatus(error: unknown): number {
  if (!error || typeof error !== 'object') return 0
  const response = (error as { response?: { status?: number } }).response
  return Number(response?.status || 0)
}

export default function RegistrationSessionStartupCoordinator() {
  useEffect(() => {
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null
    let failures = 0
    let lastAcceptedStartAt = 0

    const schedule = (delay: number) => {
      if (cancelled) return
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => void run(), delay)
    }

    const run = async () => {
      const pending = loadRegistrationSessionStartup()
      if (!pending || cancelled) return

      try {
        const server = await apiClient.getServer(pending.serverUuid)
        if (cancelled) return
        let resources = null
        let resourceObservationStatus = 0
        if (server.is_installing !== true) {
          try {
            resources = await apiClient.getServerResources(pending.serverUuid)
          } catch (error: unknown) {
            resourceObservationStatus = responseStatus(error)
            if (resourceObservationStatus !== 403) throw error
          }
        }
        if (cancelled) return

        const decision = registrationStartupDecision({
          installing: server.is_installing === true,
          suspended: server.is_suspended === true || String(server.status || '').toLowerCase() === 'suspended',
          powerState: resources?.current_state,
          resourceObservationStatus,
        })

        if (decision === 'complete' || decision === 'stop') {
          clearRegistrationSessionStartup()
          return
        }
        if (decision === 'start') {
          const cooldownRemaining = ACCEPTED_START_COOLDOWN_MS - (Date.now() - lastAcceptedStartAt)
          if (lastAcceptedStartAt && cooldownRemaining > 0) {
            schedule(Math.min(POLL_INTERVAL_MS, cooldownRemaining))
            return
          }
          await apiClient.setServerPowerState(pending.serverUuid, 'start')
          lastAcceptedStartAt = Date.now()
        }

        failures = 0
        schedule(POLL_INTERVAL_MS)
      } catch (error: unknown) {
        if (cancelled) return
        const status = responseStatus(error)
        if (registrationStartupFailureDecision(status) === 'stop') {
          clearRegistrationSessionStartup()
          return
        }
        failures += 1
        schedule(registrationStartupRetryDelay(failures))
      }
    }

    const queued = () => {
      failures = 0
      lastAcceptedStartAt = 0
      schedule(0)
    }

    window.addEventListener(REGISTRATION_STARTUP_QUEUED_EVENT, queued)
    void run()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
      window.removeEventListener(REGISTRATION_STARTUP_QUEUED_EVENT, queued)
    }
  }, [])

  return null
}
